import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { 
  Calendar, Clock, ArrowRight, Search, RefreshCw, 
  BookOpen, TrendingUp, Tag, Filter
} from "lucide-react";
import { Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [stats, setStats] = useState(null);

  const fetchArticles = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch articles from local MongoDB (populated by Robot Speed webhook)
      const params = new URLSearchParams({ limit: "50" });
      if (selectedCategory) params.append("category", selectedCategory);
      
      const response = await fetch(`${API_URL}/api/blog/articles?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setArticles(data.articles || []);
      } else {
        setArticles([]);
      }
    } catch (err) {
      console.error("Error fetching articles:", err);
      setError("Impossible de charger les articles");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/categories`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/stats`);
      const data = await response.json();
      if (data.success) {
        setStats(data);
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  useEffect(() => {
    fetchArticles();
    fetchCategories();
    fetchStats();
  }, [selectedCategory]);

  const filteredArticles = articles.filter(article =>
    article.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.excerpt?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  const estimateReadTime = (content) => {
    if (!content) return "2 min";
    const text = content.replace ? content.replace(/<[^>]*>/g, '') : content;
    const words = text.split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min`;
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Blog IA | Actualités Intelligence Artificielle | Académie IA</title>
        <meta name="description" content="Découvrez les dernières actualités, tendances et insights sur l'Intelligence Artificielle en entreprise. Articles SEO optimisés par Robot Speed." />
        <link rel="canonical" href="https://academie-ia.fr/blog" />
      </Helmet>

      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-24 pb-12 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-900/20 via-transparent to-transparent" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <BookOpen className="w-10 h-10 text-cyan-400" />
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                Blog <span className="text-cyan-400">IA</span>
              </h1>
            </div>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-4">
              Actualités, tendances et insights sur l'Intelligence Artificielle en entreprise
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-cyan-400/60">
              <TrendingUp className="w-4 h-4" />
              <span>Propulsé par Robot Speed SEO</span>
              {stats && (
                <span className="ml-2 px-2 py-1 bg-cyan-500/20 rounded-full text-xs">
                  {stats.total_published} articles
                </span>
              )}
            </div>
          </motion.div>

          {/* Search & Filter Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-3xl mx-auto mb-8"
          >
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher un article..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
                />
              </div>
              
              {/* Category Filter */}
              {categories.length > 0 && (
                <div className="relative">
                  <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="pl-10 pr-8 py-3 bg-white/5 border border-white/10 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-cyan-500 transition-colors min-w-[180px]"
                  >
                    <option value="">Toutes catégories</option>
                    {categories.map((cat) => (
                      <option key={cat.name} value={cat.name} className="bg-gray-900">
                        {cat.name} ({cat.count})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <RefreshCw className="w-12 h-12 text-cyan-400 animate-spin mb-4" />
              <p className="text-gray-400">Chargement des articles...</p>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-red-400 mb-4">{error}</p>
              <button
                onClick={fetchArticles}
                className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg transition-colors"
              >
                Réessayer
              </button>
            </div>
          ) : filteredArticles.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <BookOpen className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">
                {searchTerm ? "Aucun article trouvé" : "Aucun article pour le moment"}
              </h3>
              <p className="text-gray-400 mb-6">
                {searchTerm 
                  ? "Essayez avec d'autres mots-clés"
                  : "Les articles SEO seront bientôt publiés via Robot Speed. Revenez vite !"}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Configuration webhook: <code className="bg-white/10 px-2 py-1 rounded">/api/robot-speed/webhook</code>
              </p>
              {!searchTerm && (
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all"
                >
                  Retour à l'accueil
                  <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </motion.div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-8">
                <p className="text-gray-400">
                  {filteredArticles.length} article{filteredArticles.length > 1 ? "s" : ""} 
                  {selectedCategory && ` dans "${selectedCategory}"`}
                </p>
                <button
                  onClick={fetchArticles}
                  className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Actualiser
                </button>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article, index) => (
                  <motion.article
                    key={article.slug || article.id || index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={`/blog/${article.slug}`}
                      className="group block h-full bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-cyan-500/50 transition-all hover:shadow-xl hover:shadow-cyan-500/10"
                    >
                      {/* Article Image */}
                      {article.featured_image && (
                        <div className="aspect-video overflow-hidden">
                          <img
                            src={article.featured_image}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      )}
                      
                      <div className="p-6">
                        {/* Category Badge */}
                        {article.category && (
                          <span className="inline-block px-3 py-1 bg-cyan-500/20 text-cyan-400 text-xs font-medium rounded-full mb-3">
                            {article.category}
                          </span>
                        )}
                        
                        {/* Title */}
                        <h2 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors line-clamp-2">
                          {article.title}
                        </h2>
                        
                        {/* Excerpt */}
                        <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                          {article.excerpt || "Découvrez cet article sur l'intelligence artificielle..."}
                        </p>
                        
                        {/* Meta Info */}
                        <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                          {article.published_at && (
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(article.published_at)}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {estimateReadTime(article.content || article.excerpt)}
                          </span>
                        </div>
                        
                        {/* Tags */}
                        {article.tags && article.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-4">
                            {article.tags.slice(0, 3).map((tag, i) => (
                              <span key={i} className="flex items-center gap-1 text-xs bg-white/5 text-gray-400 px-2 py-1 rounded">
                                <Tag className="w-3 h-3" />
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Read More */}
                        <div className="flex items-center gap-2 text-cyan-400 text-sm font-medium">
                          Lire l'article
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
